import type { Dimension } from '@/Enums/ReportBuilder/Dimension.enum';
import { CustomReportChannel } from '@/Enums/ReportBuilder/ReportChannel.enum';
import { RequestAbortSignal } from '@/Enums/ReportBuilder/RequestAbortSignal.enum';
import { IClientControlOption, IControlOption } from '@/Interfaces/ControlOption.interface';
import { IFilterFormData } from '@/Interfaces/ReportBuilder/FilterFormData.interface';
import { ICustomReportListRequestPayload } from '@/Interfaces/ReportBuilder/RequestPayload.interface';
import { IScheduleFormData } from '@/Interfaces/ReportBuilder/ScheduleFormData.interface';
import { IScheduledCustomReport } from '@/Interfaces/ReportBuilder/ScheduledCustomReport.interface';
import { ICustomReportTemplate } from '@/Interfaces/ReportBuilder/Template.interface';
import ShareModal from '@/components/Modal/ShareModal';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import { METRICS_OPTIONS, getDimensionOptions } from '@/constants/reportBuilder/youtube';
import {
  getDefaultFilters,
  getReportGenerationErrorMessage,
  hydrateTemplate,
  toScheduleFormDefaults,
} from '@/helpers/reportBuilder';
import { IRequestSaverFunctions } from '@/hooks/RequestSaver';
import {
  cancelScheduledReport,
  createTemplate,
  downloadReport,
  generateReport,
  scheduleReport,
  updateScheduledReport,
} from '@/store/api/reportBuilder.api';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsSuperAdmin } from '@/store/selectors/me.selectors';
import {
  selectYoutubeCampaigns,
  selectYoutubeClients,
  selectYoutubeGeneratedReports,
  selectYoutubeScheduledReports,
  selectYoutubeTemplates,
} from '@/store/selectors/reportBuilder/youtube.selectors';
import {
  appendTemplate,
  getCampaigns,
  getGeneratedReports,
  getScheduledReports,
  getTemplates,
  loadTemplate,
} from '@/store/slices/reportBuilder/youtubeSlice';
import { ScheduledReportFilterDate } from '@/types/ReportBuilder/FilterDate.types';
import { CustomReportDownloadFunction } from '@/types/ReportBuilder/GridFunction.types';
import { SharesTargets } from '@/types/shares.types';
import { Paper } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import SaveAsTemplateModal from './Components/SaveAsTemplateModal';
import ScheduleReportModal from './Components/ScheduleReportModal';
import Filter from './Filter';
import GeneratedReportsGrid from './GeneratedReportsGrid';
import { useModal } from './Hooks/useModal';
import { useScheduledReportEditing } from './Hooks/useScheduledReportEditing';
import { useTemplateReports } from './Hooks/useTemplateReports';
import ReportPreviewGrid from './ReportPreviewGrid';
import ScheduledReportsGrid from './ScheduledReportsGrid';
import TemplateReportsGrid from './TemplateReportsGrid';

interface IProps {
  openNoticeModal: () => void;
  requestSaver: IRequestSaverFunctions;
}

const CUSTOM_REPORT_CHANNEL = CustomReportChannel.Youtube;

const YoutubeReportBuilder = ({ openNoticeModal, requestSaver }: IProps) => {
  //#region hooks
  const dispatch = useAppDispatch();
  const filterFormMethods = useForm<IFilterFormData>({
    defaultValues: getDefaultFilters(),
  });
  const { getValues, setValue, control } = filterFormMethods;
  //#endregion

  //#region state
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const { clients, clientsLoading } = useAppSelector(selectYoutubeClients);
  const { templates, loadingTemplates } = useAppSelector(selectYoutubeTemplates);
  const { campaigns, loadingCampaigns } = useAppSelector(selectYoutubeCampaigns);
  const { generatedReports, loadingGeneratedReports, generatedReportsTotal } =
    useAppSelector(selectYoutubeGeneratedReports);
  const { scheduledReports, loadingScheduledReports, scheduledReportsTotal } =
    useAppSelector(selectYoutubeScheduledReports);

  // Read at template-load time, so the callback below stays out of the options memo's ordering.
  const visibleDimensionValues = useRef<Dimension[]>([]);

  const scheduleModalState = useModal();
  const templateModalState = useModal();
  const scheduledReportEditingState = useScheduledReportEditing();
  const templateReports = useTemplateReports(CUSTOM_REPORT_CHANNEL);

  const [loadingInstantReportRun, setLoadingInstantReportRun] = useState<boolean>(false);
  //#endregion

  //#region useCallback
  const loadCampaigns = useCallback(
    (filterClients: IClientControlOption[], filterAdvertisers: IControlOption[]) => {
      dispatch(
        getCampaigns({
          clients: filterClients,
          advertisers: filterAdvertisers,
          ...requestSaver.requestSaverSetCancellation(RequestAbortSignal.Campaigns),
        }),
      );
    },
    [dispatch],
  );

  const loadGeneratedReports = useCallback(
    (payload?: ICustomReportListRequestPayload) => {
      dispatch(
        getGeneratedReports({
          ...payload,
          ...requestSaver.requestSaverSetCancellation(RequestAbortSignal.GeneratedReports),
        }),
      );
    },
    [dispatch],
  );

  const loadScheduledReports = useCallback(
    (payload?: ICustomReportListRequestPayload) => {
      dispatch(
        getScheduledReports({
          ...payload,
          ...requestSaver.requestSaverSetCancellation(RequestAbortSignal.ScheduledReports),
        }),
      );
    },
    [dispatch],
  );

  const loadTemplates = useCallback(() => {
    dispatch(getTemplates());
  }, [dispatch]);

  const loadTemplateFilters = useCallback(
    async (id: number | undefined) => {
      if (!id) {
        console.error('missing template id');
        return;
      }
      try {
        const template = (await dispatch(loadTemplate({ id })).unwrap()) as ICustomReportTemplate;
        const hydratedTemplate = hydrateTemplate(template, visibleDimensionValues.current);

        setValue('dateRange', hydratedTemplate.dateRange);
        setValue('dateRangeFormat', hydratedTemplate.dateRangeFormat);
        setValue('timeBucket', hydratedTemplate.timeBucket);
        setValue('dimensions', hydratedTemplate.dimensions ?? []);
        setValue('metrics', hydratedTemplate.metrics ?? []);
        setValue('clients', hydratedTemplate.clients);
        setValue('advertisers', hydratedTemplate.advertisers);
        setValue('campaigns', hydratedTemplate.campaigns);
        setValue('events', hydratedTemplate.events ?? []);
      } catch {
        toast.error('Failed to load the template');
      }
    },
    [dispatch, setValue],
  );

  const onSubmitReportFilters = useCallback(async () => {
    setLoadingInstantReportRun(true);
    try {
      await generateReport(getValues(), CUSTOM_REPORT_CHANNEL);
      openNoticeModal();
    } catch (error) {
      toast.error(getReportGenerationErrorMessage(error));
    }
    setLoadingInstantReportRun(false);
    loadGeneratedReports();
  }, [getValues, loadGeneratedReports, openNoticeModal]);
  //#endregion

  //#region useEffects
  useEffect(() => {
    loadTemplates();
    loadGeneratedReports();
  }, [dispatch]);
  //#endregion

  //#region data save
  const reloadTemplateReports = templateReports.reloadTemplates;
  const onCreateNewTemplate = useCallback(
    async (templateName: string) => {
      const newTemplate = await createTemplate(templateName, getValues(), CUSTOM_REPORT_CHANNEL);
      dispatch(appendTemplate(newTemplate));
      reloadTemplateReports();
    },
    [dispatch, getValues, reloadTemplateReports],
  );

  const onScheduleNewReport = useCallback(
    async (scheduleFormData: IScheduleFormData) => {
      const reportFormData = getValues();
      await scheduleReport(scheduleFormData, reportFormData, CUSTOM_REPORT_CHANNEL);
      loadScheduledReports();
      return;
    },
    [getValues, loadScheduledReports],
  );

  const onCancelScheduledReport = useCallback(
    async (id?: number) => {
      try {
        await cancelScheduledReport(id, CUSTOM_REPORT_CHANNEL);
      } catch {
        toast.error('Failed to cancel scheduled report.');
      }
      loadScheduledReports();
    },
    [loadScheduledReports],
  );

  const onEditScheduledReport = useCallback(
    (report: IScheduledCustomReport) => {
      scheduledReportEditingState.open(report);
    },
    [scheduledReportEditingState],
  );

  const onUpdateScheduledReport = useCallback(
    async (scheduleFormData: IScheduleFormData) => {
      const editingReport = scheduledReportEditingState.editingReport;
      if (!editingReport) return;
      await updateScheduledReport(editingReport.id, scheduleFormData, CUSTOM_REPORT_CHANNEL);
      loadScheduledReports();
    },
    [scheduledReportEditingState.editingReport, loadScheduledReports],
  );

  const onReportDownload = useCallback<CustomReportDownloadFunction>(
    id => downloadReport(id, CUSTOM_REPORT_CHANNEL),
    [],
  );
  //#endregion

  const templateNames = useMemo(() => {
    return templates.map(({ name }) => name);
  }, [templates]);

  const [dimensions, metrics, timeBucket] = useWatch({ control, name: ['dimensions', 'metrics', 'timeBucket'] });
  const dateRangeFormat = useWatch({ control, name: 'dateRangeFormat' }) as ScheduledReportFilterDate;

  const previewColumns = useMemo(() => [...(dimensions ?? []), ...(metrics ?? [])], [dimensions, metrics]);
  const DIMENSION_OPTIONS = useMemo(() => getDimensionOptions({ includeOrganization: isSuperAdmin }), [isSuperAdmin]);

  useEffect(() => {
    visibleDimensionValues.current = DIMENSION_OPTIONS.map(option => option.value);
  }, [DIMENSION_OPTIONS]);

  return (
    <>
      <LoadingBackdropWrapper backdropOpen={loadingInstantReportRun}>
        <FormProvider {...filterFormMethods}>
          <Filter
            clients={clients}
            campaigns={campaigns}
            loadingClients={clientsLoading}
            loadingCampaigns={loadingCampaigns}
            templates={templates}
            loadingTemplates={loadingTemplates}
            loadCampaigns={loadCampaigns}
            onSubmit={onSubmitReportFilters}
            onScheduleReportClick={scheduleModalState.onOpen}
            onSaveAsTemplateClick={templateModalState.onOpen}
            loadTemplateFilters={loadTemplateFilters}
            dimensionOptions={DIMENSION_OPTIONS}
            metricOptions={METRICS_OPTIONS}
          />
        </FormProvider>

        <Paper className='grid grid-cols-1 mt-8'>
          <ReportPreviewGrid
            visibleColumns={previewColumns}
            columns={[...DIMENSION_OPTIONS, ...METRICS_OPTIONS]}
            timeBucket={timeBucket}
          />
        </Paper>
        <Paper className='grid grid-cols-1 mt-8'>
          <GeneratedReportsGrid
            data={generatedReports}
            loading={loadingGeneratedReports}
            loadGeneratedReports={loadGeneratedReports}
            rowCount={generatedReportsTotal}
            onReportDownload={onReportDownload}
          />
        </Paper>
        <Paper className='grid grid-cols-1 mt-8'>
          <ScheduledReportsGrid
            data={scheduledReports}
            loading={loadingScheduledReports}
            onCancelScheduledReport={onCancelScheduledReport}
            onEditScheduledReport={onEditScheduledReport}
            loadScheduledReports={loadScheduledReports}
            rowCount={scheduledReportsTotal}
          />
        </Paper>
        <Paper className='grid grid-cols-1 mt-8'>
          <TemplateReportsGrid
            data={templateReports.templates}
            loading={templateReports.loading}
            onShareTemplate={templateReports.onShareTemplate}
            onDeleteTemplate={templateReports.onDeleteTemplate}
          />
        </Paper>
      </LoadingBackdropWrapper>
      <ScheduleReportModal
        onClose={
          scheduledReportEditingState.editingReport ? scheduledReportEditingState.close : scheduleModalState.onClose
        }
        open={scheduleModalState.open || !!scheduledReportEditingState.editingReport}
        mode={scheduledReportEditingState.editingReport ? 'edit' : 'create'}
        initialValues={
          scheduledReportEditingState.editingReport
            ? toScheduleFormDefaults(scheduledReportEditingState.editingReport)
            : undefined
        }
        onSubmit={scheduledReportEditingState.editingReport ? onUpdateScheduledReport : onScheduleNewReport}
        dateRangeFormat={dateRangeFormat}
      />
      <SaveAsTemplateModal
        open={templateModalState.open}
        onClose={templateModalState.onClose}
        onSubmit={onCreateNewTemplate}
        templateNames={templateNames}
      />
      <ShareModal
        open={templateReports.shareTargetId !== null}
        targetTable={SharesTargets.CUSTOM_REPORT_TEMPLATES}
        targetId={templateReports.shareTargetId ?? 0}
        title='Share Template'
        shareWithOrgHelperText='*This template will be shared with all of your team members'
        onModalClose={templateReports.onShareModalClose}
        onSubmit={templateReports.onSubmitShare}
      />
    </>
  );
};

export default YoutubeReportBuilder;
